import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import { Calculator, MessageSquare, Inbox, ArrowLeft, Phone } from "lucide-react";

const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function Admin() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, contatti: 0, preventivi: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/requests"), api.get("/stats")]);
      setRequests(r.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = requests.filter((r) => filter === "all" || r.tipo === filter);

  const cards = [
    { label: "Richieste totali", value: stats.total, icon: Inbox },
    { label: "Contatti", value: stats.contatti, icon: MessageSquare },
    { label: "Preventivi", value: stats.preventivi, icon: Calculator },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <header className="bg-brand-ink text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-extrabold">Pannello Richieste</h1>
            <p className="text-white/50 text-sm">Ristrutturare Casa Napoli</p>
          </div>
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold">
            <ArrowLeft size={16} /> Torna al sito
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-3 gap-5 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid={`stat-${c.label}`}>
              <c.icon className="text-brand-terracotta mb-3" size={22} />
              <div className="font-display text-3xl font-extrabold text-brand-ink">{c.value}</div>
              <div className="text-brand-slate/60 text-sm font-semibold">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          {[["all", "Tutte"], ["contatto", "Contatti"], ["preventivo", "Preventivi"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                filter === k ? "bg-brand-ink text-white" : "bg-white border border-gray-200 text-brand-slate hover:border-gray-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-brand-slate/60">Caricamento...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-brand-slate/60" data-testid="admin-empty">
            Nessuna richiesta ancora ricevuta.
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid={`request-${r.tipo}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                    r.tipo === "preventivo" ? "bg-brand-terracotta/10 text-brand-terracottaDark" : "bg-brand-sage/15 text-brand-sage"
                  }`}>
                    {r.tipo}
                  </span>
                  <span className="text-brand-slate/40 text-xs">{new Date(r.created_at).toLocaleString("it-IT")}</span>
                </div>
                {r.tipo === "contatto" ? (
                  <div>
                    <div className="font-display font-bold text-brand-ink text-lg">{r.dati.nome} {r.dati.cognome}</div>
                    <div className="flex items-center gap-2 text-brand-slate/70 text-sm mt-1">
                      <Phone size={14} /> {r.dati.telefono} · Fascia: {r.dati.fascia_oraria}
                    </div>
                    {r.dati.messaggio && <p className="text-brand-slate/60 text-sm mt-2">{r.dati.messaggio}</p>}
                  </div>
                ) : (
                  <div>
                    <div className="font-display font-bold text-brand-ink text-lg">
                      {euro(r.dati.stima_min)} — {euro(r.dati.stima_max)}
                    </div>
                    <div className="text-brand-slate/70 text-sm mt-1">
                      {r.dati.tipo_immobile} · {r.dati.metratura} m² · {r.dati.tipo_intervento}
                    </div>
                    <div className="text-brand-slate/60 text-sm mt-1">
                      {r.dati.numero_bagni} bagni{r.dati.cucina ? " · cucina" : ""}{r.dati.extra?.length ? ` · ${r.dati.extra.join(", ")}` : ""}
                    </div>
                    {r.dati.tempi_mesi != null && (
                      <div className="text-brand-slate/60 text-sm mt-1">
                        Durata cantiere: {r.dati.tempi_mesi} mesi
                      </div>
                    )}
                    {(r.dati.nome || r.dati.telefono) && (
                      <div className="flex items-center gap-2 text-brand-slate/70 text-sm mt-2">
                        <Phone size={14} /> {r.dati.nome} {r.dati.telefono}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
